import Papa from 'papaparse';
import type { GuestCoordinates, DimensionDefinition, PCAData } from '../types/pca';

const DATA_BASE_PATH = '/data';

// Dimension counts for different modes
const DIMS_CONFIG: Record<string, number> = {
  'enriched': 30,
  'keywords': 20,
  'all': 100,
  'guest_only': 100,
};

export async function loadGuestCoordinates(mode: 'all' | 'guest_only' | 'enriched' | 'keywords'): Promise<GuestCoordinates[]> {
  let filename: string;
  let numDims: number;
  
  if (mode === 'keywords') {
    filename = 'guest_coordinates_20d_keywords.csv';
    numDims = DIMS_CONFIG['keywords'];
  } else if (mode === 'enriched') {
    filename = 'guest_coordinates_30d_enriched.csv';
    numDims = DIMS_CONFIG['enriched'];
  } else {
    filename = mode === 'all' 
      ? 'guest_coordinates_100d_embeddings_all.csv'
      : 'guest_coordinates_100d_embeddings_guest_only.csv';
    numDims = DIMS_CONFIG[mode];
  }
  
  const url = `${DATA_BASE_PATH}/${filename}`;
  
  return new Promise((resolve, reject) => {
    Papa.parse(url, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const guests: GuestCoordinates[] = results.data.map((row: any) => {
            const guest = row.Guest || row.guest;
            const coordinates: number[] = [];
            
            // Extract all dimension values
            for (let i = 1; i <= numDims; i++) {
              const dimKey = `Dim${i}`;
              const value = parseFloat(row[dimKey] || '0');
              coordinates.push(isNaN(value) ? 0 : value);
            }
            
            // Extract cluster if present
            const cluster = row.Cluster ? parseInt(row.Cluster, 10) : undefined;
            
            return { guest, coordinates, cluster };
          }).filter((g: GuestCoordinates) => g.guest && g.coordinates.length === numDims);
          
          resolve(guests);
        } catch (error) {
          reject(new Error(`Failed to parse CSV: ${error}`));
        }
      },
      error: (error) => {
        reject(new Error(`Failed to load CSV: ${error.message}`));
      }
    });
  });
}

export async function loadDimensionDefinitions(mode: 'all' | 'guest_only' | 'enriched' | 'keywords'): Promise<Record<string, DimensionDefinition>> {
  let filename: string;
  
  if (mode === 'keywords') {
    filename = 'dimension_labels_20d_keywords.json';
  } else if (mode === 'enriched') {
    filename = 'dimension_labels_30d_enriched.json';
  } else {
    filename = mode === 'all'
      ? 'dimension_definitions_100d_embeddings_all.json'
      : 'dimension_definitions_100d_embeddings_guest_only.json';
  }
  
  const url = `${DATA_BASE_PATH}/${filename}`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data as Record<string, DimensionDefinition>;
  } catch (error) {
    throw new Error(`Failed to load dimension definitions: ${error}`);
  }
}

export async function loadPCAData(mode: 'all' | 'guest_only' | 'enriched' | 'keywords'): Promise<PCAData> {
  const [guestCoordinates, dimensionDefinitions] = await Promise.all([
    loadGuestCoordinates(mode),
    loadDimensionDefinitions(mode)
  ]);
  
  return {
    guestCoordinates,
    dimensionDefinitions,
    mode: (mode === 'enriched' || mode === 'keywords') ? 'all' : mode
  };
}
